import React from "react";
import styled from "styled-components";
import moment from "moment";
import { DayPickerSingleDateController } from "react-dates";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { CalendarDay } from "@sonder/ui/source/styled/components/DateTimePicker/CalendarDay";
import { FooterSection } from "@sonder/ui/source/styled/components/DateTimePicker/Footer";
import {
  MonthElement,
  NextMonthButton,
  PreviousMonthButton,
} from "@sonder/ui/source/styled/components/DateTimePicker/Header";
import { TimePicker } from "@sonder/ui/source/styled/components/TimePicker";

interface PartialRecurrenceSetting {
  daysOfWeek: string[] | null;
  intervalAmt: number;
  intervalUnit: "day" | "week" | "month";
}
const isDayRecurring = (
  day: moment.Moment | null,
  selectedDate: moment.Moment,
  recurrenceSettings?: PartialRecurrenceSetting | null,
) => {
  const { daysOfWeek, intervalAmt, intervalUnit } = recurrenceSettings || {};
  if (!day || !daysOfWeek || !intervalAmt || intervalUnit !== "week") {
    return false;
  }

  const isAfterSelectedDate = day.isAfter(selectedDate);
  const isOnDayOfWeek = daysOfWeek.includes(day.format("dddd").toLowerCase());
  const numberOfWeeksSinceSelectedDate = day.diff(
    selectedDate.clone().subtract(1, "day"),
    "week",
  );

  return (
    isAfterSelectedDate &&
    isOnDayOfWeek &&
    numberOfWeeksSinceSelectedDate > 0 &&
    numberOfWeeksSinceSelectedDate % intervalAmt === 0
  );
};

export const Wrapper = styled.div`
  align-items: center;
  border-radius: 0.3rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  line-height: 1rem;
  min-height: 19rem;
  width: 23rem;
`;

interface DateTimePickerProps {
  children?: React.ReactNode;
  className?: string;
  date: moment.Moment | null;
  isOutsideRange?: (day: moment.Moment) => boolean;
  onCloseTimePicker?(): void;
  onDateChange?(nextDate: moment.Moment): void;
  onFocusChange?({ focused }: { focused: boolean | null }): void;
  onOpenTimePicker?(): void;
  onTimeChange?(nextDate: moment.Moment): void;
  recurrenceSettings?: PartialRecurrenceSetting | null;
  showTimePicker?: boolean;
}
export const DateTimePicker = ({
  children,
  className,
  date,
  isOutsideRange = day => {
    return day ? day.isBefore(moment().startOf("day")) : true;
  },
  recurrenceSettings,
  showTimePicker = true,
  onDateChange,
  onTimeChange,
  onFocusChange = () => undefined,
  onCloseTimePicker = () => undefined,
  onOpenTimePicker = () => undefined,
}: DateTimePickerProps) => {
  const dateWithDefault = date ? moment(date) : moment().endOf("day");

  return (
    <Wrapper className={className}>
      <DayPickerSingleDateController
        focused
        hideKeyboardShortcutsPanel
        keepOpenOnDateSelect
        noBorder
        date={dateWithDefault}
        isOutsideRange={isOutsideRange}
        navNext={<NextMonthButton />}
        navPrev={<PreviousMonthButton />}
        numberOfMonths={1}
        renderCalendarDay={({ day, ...rest }) => (
          <CalendarDay
            {...rest}
            day={day}
            isDisabledDay={day ? isOutsideRange(day) : true}
            isRecurrenceDay={isDayRecurring(
              day,
              dateWithDefault,
              recurrenceSettings,
            )}
          />
        )}
        renderMonthElement={MonthElement}
        showKeyboardShortcuts={false}
        onDateChange={nextDate => {
          if (onDateChange && nextDate) {
            // react-dates sets the timestamp to 12:00 PM, but we want to keep
            // the timestamp set by the user via TimePicker
            let timestamp = nextDate;
            if (date) {
              timestamp = nextDate
                .clone()
                .hour(date.hour())
                .minute(date.minute())
                .seconds(date.seconds());
            }
            onDateChange(timestamp);
          }
        }}
        onFocusChange={onFocusChange}
      />
      {showTimePicker && (
        <FooterSection>
          <TimePicker
            date={dateWithDefault}
            onChange={onTimeChange}
            onClose={onCloseTimePicker}
            onOpen={onOpenTimePicker}
          />
        </FooterSection>
      )}
      {children}
    </Wrapper>
  );
};
